import * as XLSX from 'xlsx-js-style';
import { validateChequeNumber } from '../../utils/chequeValidators';

const SHEET_NAME = 'History Data';
const REQUIRED_COLUMNS = ['Bank Account Name', 'Cheque Number', 'Beneficiary Name', 'Amount', 'Cheque Date', 'Status'];
const VALID_STATUSES = ['Printed', 'Cleared', 'Cancelled'];

const parseDate = (rawDate) => {
  if (!rawDate) return { date: '', error: 'Date is required' };

  const dateStr = String(rawDate).trim();

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const parsed = new Date(dateStr);
    if (isNaN(parsed.getTime())) return { date: '', error: 'Invalid date' };
    return { date: dateStr, error: null };
  }

  // DD-MM-YYYY or DD/MM/YYYY
  if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(dateStr)) {
    const parts = dateStr.split(/[-/]/);
    const iso = `${parts[2]}-${parts[1]}-${parts[0]}`;
    const parsed = new Date(iso);
    if (isNaN(parsed.getTime())) return { date: '', error: 'Invalid date' };
    return { date: iso, error: null };
  }

  // Excel serial number
  if (!isNaN(Number(dateStr))) {
    const excelDate = XLSX.SSF.parse_date_code(Number(dateStr));
    if (excelDate) {
      const y = excelDate.y;
      const m = String(excelDate.m).padStart(2, '0');
      const d = String(excelDate.d).padStart(2, '0');
      return { date: `${y}-${m}-${d}`, error: null };
    }
  }

  return { date: '', error: 'Invalid date format. Use YYYY-MM-DD or DD-MM-YYYY' };
};

export const chequeHistoryImportService = {
  generateTemplate: () => {
    const templateData = [
      {
        'Bank Account Name': 'Main Business Account',
        'Cheque Number': '000001',
        'Beneficiary Name': 'John Smith',
        'Amount': 5000,
        'Cheque Date': '2025-12-01',
        'Status': 'Printed',
        'Cleared Date': '',
        'Remarks': 'Monthly payment'
      },
      {
        'Bank Account Name': 'Main Business Account',
        'Cheque Number': '000002',
        'Beneficiary Name': 'ABC Supplies Ltd',
        'Amount': 12500.50,
        'Cheque Date': '2025-11-15',
        'Status': 'Cleared',
        'Cleared Date': '2025-11-20',
        'Remarks': 'Invoice #1234'
      },
      {
        'Bank Account Name': 'Payroll Account',
        'Cheque Number': '000003',
        'Beneficiary Name': 'XYZ Services',
        'Amount': 3200,
        'Cheque Date': '2025-10-01',
        'Status': 'Cancelled',
        'Cleared Date': '',
        'Remarks': 'Cancelled - wrong amount'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData, {
      header: Object.keys(templateData[0])
    });

    const instructions = [
      { Field: 'Bank Account Name', Description: 'Name of the bank account (Required). Must match an existing bank in your company.', Example: 'Main Business Account', Notes: 'Case-insensitive match' },
      { Field: 'Cheque Number', Description: 'Cheque number (Required). Must be unique per bank.', Example: '000001', Notes: 'Numeric, max 6 digits' },
      { Field: 'Beneficiary Name', Description: 'Name of the payee (Required).', Example: 'John Smith', Notes: 'Max 200 characters' },
      { Field: 'Amount', Description: 'Cheque amount (Required). Must be greater than 0.', Example: '5000', Notes: 'Decimal allowed' },
      { Field: 'Cheque Date', Description: 'Date on the cheque (Required).', Example: '2025-12-01', Notes: 'YYYY-MM-DD or DD-MM-YYYY' },
      { Field: 'Status', Description: 'Cheque status (Required).', Example: 'Printed', Notes: 'Printed, Cleared, or Cancelled' },
      { Field: 'Cleared Date', Description: 'Date cheque was cleared (Required if Status is Cleared).', Example: '2025-12-05', Notes: 'Must not be in the future' },
      { Field: 'Remarks', Description: 'Additional notes (Optional).', Example: 'Monthly rent', Notes: 'Max 500 characters' }
    ];

    const instructionsWs = XLSX.utils.json_to_sheet(instructions);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, instructionsWs, 'Instructions');
    XLSX.utils.book_append_sheet(wb, ws, SHEET_NAME);

    ws['!cols'] = [
      { wch: 25 }, // Bank Account Name
      { wch: 15 }, // Cheque Number
      { wch: 25 }, // Beneficiary Name
      { wch: 15 }, // Amount
      { wch: 15 }, // Cheque Date
      { wch: 12 }, // Status
      { wch: 15 }, // Cleared Date
      { wch: 30 }  // Remarks
    ];

    instructionsWs['!cols'] = [
      { wch: 20 },
      { wch: 60 },
      { wch: 25 },
      { wch: 25 }
    ];

    return wb;
  },

  downloadTemplate: () => {
    const wb = chequeHistoryImportService.generateTemplate();
    const fileName = `Cheque_History_Import_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  },

  validateFileStructure: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          if (!workbook.SheetNames.includes(SHEET_NAME)) {
            reject(new Error(`Excel file must contain a "${SHEET_NAME}" sheet. Found sheets: ${workbook.SheetNames.join(', ')}`));
            return;
          }

          const worksheet = workbook.Sheets[SHEET_NAME];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            reject(new Error('The "History Data" sheet is empty'));
            return;
          }

          const firstRow = jsonData[0];
          const missingFields = REQUIRED_COLUMNS.filter(field => !(field in firstRow));

          if (missingFields.length > 0) {
            reject(new Error(`Missing required columns: ${missingFields.join(', ')}`));
            return;
          }

          resolve({
            isValid: true,
            data: jsonData,
            rowCount: jsonData.length
          });
        } catch (error) {
          reject(new Error('Invalid Excel file format'));
        }
      };
      reader.readAsArrayBuffer(file);
    });
  },

  parseAndValidateRows: (rawData, activeBanks) => {
    // Build bank lookup (case-insensitive)
    const bankLookup = {};
    activeBanks.forEach(bank => {
      bankLookup[bank.accountName.toLowerCase().trim()] = bank;
    });

    // Track cheque numbers per bank for intra-file duplicate detection
    const seenChequeNumbers = {}; // key: `${bankId}-${chequeNumber}`

    return rawData.map((row, index) => {
      const errors = [];
      const rowNumber = index + 1;

      // Validate bank account name
      const bankAccountName = row['Bank Account Name'] ? String(row['Bank Account Name']).trim() : '';
      let matchedBank = null;
      if (!bankAccountName) {
        errors.push('Bank Account Name is required');
      } else {
        matchedBank = bankLookup[bankAccountName.toLowerCase()];
        if (!matchedBank) {
          errors.push(`Bank "${bankAccountName}" not found. Available banks: ${activeBanks.map(b => b.accountName).join(', ')}`);
        }
      }

      // Validate cheque number (required for history)
      let chequeNumber = '';
      const rawChequeNumber = row['Cheque Number'];
      if (rawChequeNumber === undefined || rawChequeNumber === null || String(rawChequeNumber).trim() === '') {
        errors.push('Cheque Number is required for history import');
      } else {
        chequeNumber = String(rawChequeNumber).trim();
        const validation = validateChequeNumber(chequeNumber);
        if (!validation.isValid) {
          errors.push(validation.message);
        } else {
          // Zero-pad to 6 digits (e.g. "1" → "000001", "123" → "000123")
          chequeNumber = chequeNumber.padStart(6, '0');
          // Check intra-file duplicates
          if (matchedBank) {
            const dupKey = `${matchedBank.id}-${chequeNumber}`;
            if (seenChequeNumbers[dupKey]) {
              errors.push(`Duplicate cheque number ${chequeNumber} for bank "${bankAccountName}" in this file (row ${seenChequeNumbers[dupKey]})`);
            } else {
              seenChequeNumbers[dupKey] = rowNumber;
            }
          }
        }
      }

      // Validate beneficiary name
      const beneficiaryName = row['Beneficiary Name'] ? String(row['Beneficiary Name']).trim() : '';
      if (!beneficiaryName) {
        errors.push('Beneficiary Name is required');
      } else if (beneficiaryName.length > 200) {
        errors.push('Beneficiary Name must be 200 characters or less');
      }

      // Validate amount
      const rawAmount = row['Amount'];
      const chqAmount = parseFloat(rawAmount);
      if (rawAmount === undefined || rawAmount === null || rawAmount === '') {
        errors.push('Amount is required');
      } else if (isNaN(chqAmount) || chqAmount <= 0) {
        errors.push('Amount must be a number greater than 0');
      }

      // Validate cheque date (past dates allowed for history)
      const chqDateResult = parseDate(row['Cheque Date']);
      if (chqDateResult.error) {
        errors.push(`Cheque Date: ${chqDateResult.error}`);
      }

      // Validate status
      const rawStatus = row['Status'] ? String(row['Status']).trim() : '';
      let status = '';
      if (!rawStatus) {
        errors.push('Status is required');
      } else {
        // Case-insensitive match but store proper case
        const matched = VALID_STATUSES.find(s => s.toLowerCase() === rawStatus.toLowerCase());
        if (!matched) {
          errors.push(`Status must be one of: ${VALID_STATUSES.join(', ')}`);
        } else {
          status = matched;
        }
      }

      // Validate cleared date
      let clearedDate = '';
      if (status === 'Cleared') {
        const rawClearedDate = row['Cleared Date'];
        if (!rawClearedDate || String(rawClearedDate).trim() === '') {
          errors.push('Cleared Date is required when Status is Cleared');
        } else {
          const clearedDateResult = parseDate(rawClearedDate);
          if (clearedDateResult.error) {
            errors.push(`Cleared Date: ${clearedDateResult.error}`);
          } else {
            // Check not in the future
            const clearedDateObj = new Date(clearedDateResult.date);
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            if (clearedDateObj > today) {
              errors.push('Cleared Date cannot be in the future');
            } else {
              clearedDate = clearedDateResult.date;
            }
          }
        }
      }

      // Validate remarks (optional)
      const remarks = row['Remarks'] ? String(row['Remarks']).trim() : '';
      if (remarks.length > 500) {
        errors.push('Remarks must be 500 characters or less');
      }

      return {
        rowNumber,
        data: row,
        isValid: errors.length === 0,
        errors,
        parsed: {
          bankAccountName,
          bankId: matchedBank ? matchedBank.id : null,
          bankName: matchedBank ? matchedBank.accountName : '',
          currency: matchedBank ? matchedBank.currency : '',
          chequeNumber,
          beneficiaryName,
          chqAmount: isNaN(chqAmount) ? 0 : chqAmount,
          chqDate: chqDateResult.date || '',
          status,
          clearedDate,
          remarks
        }
      };
    });
  },

  generateResultFile: (results) => {
    const resultData = results.map(result => ({
      'Row Number': result.rowNumber,
      'Bank Account Name': result.data['Bank Account Name'] || '',
      'Cheque Number': result.data['Cheque Number'] || '',
      'Beneficiary Name': result.data['Beneficiary Name'] || '',
      'Amount': result.data['Amount'] || '',
      'Cheque Date': result.data['Cheque Date'] || '',
      'Status': result.data['Status'] || '',
      'Cleared Date': result.data['Cleared Date'] || '',
      'Remarks': result.data['Remarks'] || '',
      'Import Status': result.importStatus || (result.isValid ? 'VALID' : 'INVALID'),
      'Error Details': result.importMessage || result.errors.join('; ')
    }));

    const ws = XLSX.utils.json_to_sheet(resultData);
    ws['!cols'] = [
      { wch: 10 },  // Row Number
      { wch: 25 },  // Bank Account Name
      { wch: 15 },  // Cheque Number
      { wch: 25 },  // Beneficiary Name
      { wch: 15 },  // Amount
      { wch: 15 },  // Cheque Date
      { wch: 12 },  // Status
      { wch: 15 },  // Cleared Date
      { wch: 25 },  // Remarks
      { wch: 15 },  // Import Status
      { wch: 50 }   // Error Details
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Import Results');

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T');
    const fileName = `Cheque_History_Import_Results_${timestamp[0]}_${timestamp[1].split('.')[0]}.xlsx`;

    return { workbook: wb, fileName };
  },

  downloadResultFile: (results) => {
    const { workbook, fileName } = chequeHistoryImportService.generateResultFile(results);
    XLSX.writeFile(workbook, fileName);
  }
};
